import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  setPersistence,
  browserLocalPersistence,
  type UserCredential,
  sendPasswordResetEmail,
  sendEmailVerification,
  deleteUser,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  updateEmail,
  type User,
} from "firebase/auth";
import { firebaseAuth } from "@/lib/firebase-config";
import axiosClient from "@/lib/axios/axiosClient";
import type {
  UpdateUserAPIRequest,
  UpdateUserAPIResponse,
} from "@/common/types/user";
import type { PlanName } from "@/common/enums/enums";
import { FirebaseError } from "firebase/app";
import { AUTH_ERRORS } from "@/common/errors";

void setPersistence(firebaseAuth, browserLocalPersistence);

export interface LoginFormValues {
  email: string;
  password: string;
}

export const signInUserWithEmail = async ({
  email,
  password,
}: LoginFormValues): Promise<UserCredential> => {
  const result = await signInWithEmailAndPassword(
    firebaseAuth,
    email,
    password
  );
  return result;
};

export interface UserFormValues {
  email: string;
  password: string;
  displayName?: string;
}

export const signupUserWithEmail = async ({
  email,
  password,
}: UserFormValues): Promise<UserCredential> => {
  const result = await createUserWithEmailAndPassword(
    firebaseAuth,
    email,
    password
  );

  if (result?.user && !result.user.emailVerified) {
    await sendEmailVerification(result?.user);
  }

  return result;
};

export const signOutUser = async (): Promise<void> => {
  await signOut(firebaseAuth);
};

export const getCurrentUser = (): Promise<User | null> => {
  return new Promise((resolve) => {
    const unsubscribe = firebaseAuth.onAuthStateChanged((user) => {
      unsubscribe();
      resolve(user);
    });
  });
};

export const deleteUserAuth = async (): Promise<void> => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    await deleteUser(currentUser);
  }
};

export const checkEmailVerified = async (): Promise<boolean> => {
  const user = await getCurrentUser();
  return user?.emailVerified || false;
};

export const sendPasswordResetWithEmail = async ({
  email,
}: Pick<LoginFormValues, "email">): Promise<void> => {
  await sendPasswordResetEmail(firebaseAuth, email);
};

export const changePasswordAuth = async (
  newPassword: string
): Promise<void> => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    await updatePassword(currentUser, newPassword);
  }
};

export const changeEmailAuth = async (newEmail: string): Promise<void> => {
  const currentUser = firebaseAuth.currentUser;
  if (currentUser) {
    await updateEmail(currentUser, newEmail);
  }
};

export const sendEmailVerificationAuth = async (): Promise<void> => {
  const currentUser = await getCurrentUser();
  if (currentUser) {
    await sendEmailVerification(currentUser);
  }
};

export const verifyPassword = async (password: string): Promise<boolean> => {
  try {
    const currentUser = await getCurrentUser();
    if (currentUser) {
      const credential = EmailAuthProvider.credential(
        currentUser.email!,
        password
      );
      await reauthenticateWithCredential(currentUser, credential);
      return true;
    }
    return false;
  } catch (error) {
    if (error instanceof FirebaseError) {
      return error.code === AUTH_ERRORS.MULTI_FACTOR_REQUIRED;
    }
    return false;
  }
};

export interface UserPreference {
  title?: string;
  occupation?: string;
  country?: string;
  city?: string;
  state?: string;
  transcriptionLanguageId?: number;
}

export interface UserData {
  id: number;
  createDate: string;
  email: string;
  firstname: string;
  lastname: string;
  phone: string;
  status: number;
  stripeCustomerId: string;
  planName: PlanName;
  planStatus: string;
  periodEnd: number;
  periodEndCancel: number;
  style: string;
  format: string;
  recordingUUID: string;
  recordingTime: string;
  retentionPeriod: number;
  deleteReason: string;
  deleteDate: string;
  deleteCompleteDate: string;
  f_uid: string | null;
  userPreference: UserPreference;
}

export const getUser = async ({
  email,
}: Pick<LoginFormValues, "email">): Promise<{
  error: boolean;
  user: UserData | null;
}> => {
  try {
    const response = await axiosClient.post<UserData[]>(
      "/get-user",
      { email },
      {
        sendToken: true,
        headers: {
          Accept: "application/json",
          "Content-Type": "text/plain; charset=utf-8",
        },
      }
    );

    const user = response?.data?.[0] || null;

    if (response.status === 200) {
      if (user) {
        return { error: false, user };
      } else {
        return { error: true, user };
      }
    } else {
      return { error: true, user };
    }
  } catch {
    return { error: true, user: null };
  }
};

export interface AddUserParameters {
  email: string;
  firstname: string;
  lastname: string;
  fUid: string;
  occupationId: number;
}

export const addUser = async ({
  email,
  firstname,
  lastname,
  fUid,
  occupationId,
}: AddUserParameters): Promise<{
  message: string;
  userId: number;
}> => {
  const response = await axiosClient.post<{
    message: string;
    userId: number;
  }>(
    "/add-user",
    { email, firstname, lastname, fUid, occupationId },
    {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    }
  );
  return response.data;
};

export const updateUser = async (
  data: UpdateUserAPIRequest
): Promise<UpdateUserAPIResponse> => {
  const response = await axiosClient.post<UpdateUserAPIResponse>(
    "/update-user",
    data,
    {
      sendToken: true,
      headers: {
        Accept: "application/json",
        "Content-Type": "text/plain; charset=utf-8",
      },
    }
  );
  return response.data;
};
